import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Input, Switch, Select, Alert, message } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller, useWatch } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";

const schema = yup.object().shape({
  placement: yup.string().nullable(),
  is_blocked: yup.boolean(),
  blocked_reason: yup.string().nullable(),
  location_id: yup.number().nullable(),
});

const DeviceForm = ({
  isModalOpen,
  editingScreen,
  closeModal,
  handleUpdateList,
  facilities = { type: "flat", locations: [] },
}) => {
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dataSet, setDataSet] = useState(null);
  const [initialLocationId, setInitialLocationId] = useState(null);

  // Buildings with floors: only the floors are selectable options, grouped
  // under the building via OptGroup. A standalone building with no floors
  // of its own is selectable directly, since there's nothing else to pick.
  // Mirrors InventoryForm.jsx's groupedFacilityOptions - grouping only
  // applies when facilities.type is "grouped" (user has no specific
  // location assignments); otherwise the user's own flat location list is
  // shown as-is.
  const groupedLocationOptions = useMemo(() => {
    if (facilities.type !== "grouped") return null;
    const all = facilities.locations || [];
    const buildings = all.filter((loc) => !loc.parent_id);
    return buildings.map((building) => {
      const floors = all.filter((loc) => loc.parent_id === building.id);
      return { building, floors };
    });
  }, [facilities]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingScreen },
    defaultValues: {
      placement: "",
      is_blocked: false,
      blocked_reason: "",
      location_id: null,
    },
  });

  const isBlocked = useWatch({ control, name: "is_blocked" });

  const fetchData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/devices/${id}`
      );
      setDataSet(response.data.data);

      // Set the form values to the fetched data
      setValue("placement", response.data.data.placement || "");
      setValue("is_blocked", response.data.data.is_blocked === "yes");
      setValue("blocked_reason", response.data.data.blocked_reason || "");
      setValue("location_id", response.data.data.location_id || null);
      setInitialLocationId(response.data.data.location_id || null);

      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editingScreen?.id) {
      fetchData(editingScreen.id);
    }
  }, [editingScreen]);

  const onSubmitScreen = async (formData) => {
    setSubmitting(true);
    setShowErrors(false);
    
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/devices/updatePlacement`,
        {
          id: editingScreen.id,
          placement: formData.placement || null,
        }
      );
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/devices/updateBlock`,
        {
          id: editingScreen.id,
          is_blocked: formData.is_blocked,
          reason: formData.blocked_reason || null,
        }
      );
      // Only hit updateLocation when it actually changed - it regenerates
      // the device's code every time it's called, so it shouldn't fire on
      // every unrelated edit (placement/block toggles) to this form.
      if (formData.location_id && formData.location_id !== initialLocationId) {
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}/api/devices/updateLocation`,
          {
            id: editingScreen.id,
            location_id: formData.location_id,
          }
        );
      }
      await handleUpdateList();
      message.success("Device updated");
      closeModal();
    } catch (err) {
      console.log(err);
      setErrorsMessage(
        err.response?.data?.message || "An error occurred while updating the device"
      );
      setShowErrors(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    reset();
    setShowErrors(false);
    setErrorsMessage("");
    closeModal();
  };

  return (
    <Modal
      title={"Update Device"}
      open={isModalOpen}
      onCancel={handleModalClose}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <div className="col-sm-12 mb-3">
              <Alert message={errorsMessage} type="error" closeIcon={false} />
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmitScreen)}>
            <div className="mb-3">
              <label className="form-label me-3">Placement</label>
              <Controller
                name="placement"
                control={control}
                render={({ field }) => (
                  <Input {...field} placeholder="e.g. Reception - wall mounted" />
                )}
              />
              {errors.placement && (
                <div className="text-danger small">{errors.placement.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label me-3">Location</label>
              <Controller
                name="location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    placeholder="Select a location"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {facilities.type === "flat"
                      ? (facilities.locations || []).map((loc) => (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.name}
                            {loc.city_name ? ` (${loc.city_name})` : ""}
                          </Select.Option>
                        ))
                      : groupedLocationOptions.map(({ building, floors }) =>
                          floors.length > 0 ? (
                            <Select.OptGroup key={building.id} label={building.name}>
                              {floors.map((floor) => (
                                <Select.Option key={floor.id} value={floor.id}>
                                  {floor.name}
                                </Select.Option>
                              ))}
                            </Select.OptGroup>
                          ) : (
                            <Select.Option key={building.id} value={building.id}>
                              {building.name}
                              {building.city_name ? ` (${building.city_name})` : ""}
                            </Select.Option>
                          )
                        )}
                  </Select>
                )}
              />
              {errors.location_id && (
                <div className="text-danger small">{errors.location_id.message}</div>
              )}
              <div className="text-muted small mt-1">
                Changing this regenerates the device's code.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label me-3">Block Device</label>
              <Controller
                name="is_blocked"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren="Blocked"
                    unCheckedChildren="Not Blocked"
                  />
                )}
              />
              {isBlocked && (
                <div className="mt-2">
                  <label className="form-label me-3">Reason (shown to the device)</label>
                  <Controller
                    name="blocked_reason"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} placeholder="e.g. Overdue payment" />
                    )}
                  />
                </div>
              )}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update"}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default DeviceForm;